import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, FileText, Download, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const DELIVERY_FEE = 24;
const DELIVERY_THRESHOLD = 1400;
const TVA_RATE = 0.20;

const TIER_ORDER = ["1","2","3","4","5","5-9","10-19","20-39","40-99","100-199","200+"];

function getTierKey(qty) {
  if (qty >= 200) return "200+";
  if (qty >= 100) return "100-199";
  if (qty >= 40) return "40-99";
  if (qty >= 20) return "20-39";
  if (qty >= 10) return "10-19";
  if (qty >= 5) return "5-9";
  return String(qty);
}

export default function Quotes() {
  const { t } = useLanguage();
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [pricings, setPricings] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [lines, setLines] = useState([{ product_id: '', product_name: '', quantity: 1 }]);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [qRes, pRes, prRes] = await Promise.all([
        axios.get(`${API}/api/quotes`, { withCredentials: true }),
        axios.get(`${API}/api/products`, { withCredentials: true }),
        axios.get(`${API}/api/quotes/product-pricing`, { withCredentials: true })
      ]);
      setQuotes(qRes.data);
      setProducts(pRes.data);
      const map = {};
      prRes.data.forEach(p => { map[p.product_id] = p.tiers || {}; });
      setPricings(map);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getUnitPrice = (productId, qty) => {
    const tiers = pricings[productId] || {};
    const key = getTierKey(qty);
    return parseFloat(tiers[key] || 0);
  };

  const calcTotals = () => {
    let subtotal = 0;
    lines.forEach(l => {
      if (l.product_id && l.quantity > 0) {
        subtotal += getUnitPrice(l.product_id, l.quantity) * l.quantity;
      }
    });
    const delivery = subtotal < DELIVERY_THRESHOLD && subtotal > 0 ? DELIVERY_FEE : 0;
    const totalHT = subtotal + delivery;
    const tva = totalHT * TVA_RATE;
    const ttc = totalHT + tva;
    return { subtotal, delivery, totalHT, tva, ttc };
  };

  const addLine = () => setLines([...lines, { product_id: '', product_name: '', quantity: 1 }]);

  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== idx));
  };

  const updateLine = (idx, field, value) => {
    const updated = [...lines];
    updated[idx][field] = value;
    if (field === 'product_id') {
      const prod = products.find(p => p.id === value);
      updated[idx].product_name = prod ? prod.name : '';
    }
    setLines(updated);
  };

  const handleCreate = async () => {
    if (!clientName.trim()) { toast.error('Nom du client requis'); return; }
    const validLines = lines.filter(l => l.product_id && l.quantity > 0);
    if (validLines.length === 0) { toast.error('Ajoutez au moins un produit'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/api/quotes`, { client_name: clientName, client_email: clientEmail, lines: validLines }, { withCredentials: true });
      toast.success(t('quote_created'));
      setShowForm(false);
      setClientName('');
      setClientEmail('');
      setLines([{ product_id: '', product_name: '', quantity: 1 }]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setCreating(false); }
  };

  const handleDownloadPDF = async (quoteId) => {
    try {
      const res = await axios.get(`${API}/api/quotes/${quoteId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Devis_${quoteId}.pdf`;

      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur PDF'); }
  };

  const handleSendEmail = async (quoteId) => {
    setSending(quoteId);
    try {
      const res = await axios.post(`${API}/api/quotes/${quoteId}/send-email`, {}, { withCredentials: true });
      toast.success(res.data.message || t('quote_sent'));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
    finally { setSending(null); }
  };

  const totals = calcTotals();

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{t('quotes_title')}</h1>
            <p className="text-sm text-slate-600 mt-1">{t('quotes_desc')}</p>
          </div>
          <Button data-testid="new-quote-btn" onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus size={16} className="mr-2" /> {t('new_quote')}
          </Button>
        </div>

        {/* Create Quote Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileText size={20} /> {t('new_quote')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">{t('quote_client')}</Label>
                <Input data-testid="quote-client-name" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Nom du client / club" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t('client_email_label')}</Label>
                <Input data-testid="quote-client-email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="email@client.com" type="email" />
                <p className="text-xs text-slate-400 mt-1">{t('client_email_hint')}</p>
              </div>

              {/* Product Lines */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Produits</Label>
                {lines.map((line, idx) => (
                  <div key={idx} className="flex items-end gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">{t('select_product')}</Label>
                      <Select value={line.product_id} onValueChange={v => updateLine(idx, 'product_id', v)}>
                        <SelectTrigger data-testid={`quote-product-${idx}`}><SelectValue placeholder={t('select_product')} /></SelectTrigger>
                        <SelectContent>
                          {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24">
                      <Label className="text-xs text-slate-500">{t('quantity')}</Label>
                      <Input data-testid={`quote-qty-${idx}`} type="number" min="1" value={line.quantity} onChange={e => updateLine(idx, 'quantity', parseInt(e.target.value) || 1)} />
                    </div>
                    <div className="w-28 text-center">
                      <Label className="text-xs text-slate-500">{t('unit_price')}</Label>
                      <p className="text-sm font-medium py-2">{line.product_id ? `${getUnitPrice(line.product_id, line.quantity).toFixed(2)} €` : '-'}</p>
                    </div>
                    <div className="w-28 text-center">
                      <Label className="text-xs text-slate-500">{t('line_total')}</Label>
                      <p className="text-sm font-bold py-2">{line.product_id ? `${(getUnitPrice(line.product_id, line.quantity) * line.quantity).toFixed(2)} €` : '-'}</p>
                    </div>
                    {lines.length > 1 && (
                      <Button size="sm" variant="ghost" className="text-red-600 h-9" onClick={() => removeLine(idx)}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addLine} className="gap-1">
                  <Plus size={14} /> {t('add_line')}
                </Button>
              </div>

              {/* Totals */}
              <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-2">
                <div className="flex justify-between text-sm"><span>{t('subtotal_ht')}</span><span className="font-medium">{totals.subtotal.toFixed(2)} €</span></div>
                <div className="flex justify-between text-sm">
                  <span>{t('delivery_fee')} {totals.delivery === 0 && totals.subtotal > 0 ? <Badge className="bg-emerald-100 text-emerald-700 text-xs ml-1">{t('delivery_free')}</Badge> : ''}</span>
                  <span className="font-medium">{totals.delivery.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-2"><span className="font-semibold">{t('total_ht')}</span><span className="font-semibold">{totals.totalHT.toFixed(2)} €</span></div>
                <div className="flex justify-between text-sm"><span>{t('tva')} (20%)</span><span className="font-medium">{totals.tva.toFixed(2)} €</span></div>
                <div className="flex justify-between text-lg border-t pt-2"><span className="font-bold">{t('total_ttc')}</span><span className="font-bold text-slate-900">{totals.ttc.toFixed(2)} €</span></div>
                <p className="text-xs text-slate-400 mt-1">{t('delivery_fee_info')}</p>
              </div>

              <Button data-testid="create-quote-btn" onClick={handleCreate} disabled={creating} className="w-full bg-slate-900 hover:bg-slate-800">
                {creating ? '...' : t('create_quote')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quotes List */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-lg font-medium">{t('my_quotes')}</CardTitle>
              <Input
                data-testid="quote-search"
                placeholder={t('search_quotes_placeholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="max-w-sm"
              />
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const q = searchQuery.toLowerCase().trim();
              const filtered = q ? quotes.filter(quote =>
                (quote.quote_number || '').toLowerCase().includes(q) ||
                (quote.client_name || '').toLowerCase().includes(q) ||
                (quote.client_phone || '').toLowerCase().includes(q) ||
                (quote.client_email || '').toLowerCase().includes(q)
              ) : quotes;
              return filtered.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{q ? t('no_search_results') : t('no_quotes')}</p>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t('quote_number')}</TableHead>
                    <TableHead>{t('quote_client')}</TableHead>
                    <TableHead>{t('quote_date')}</TableHead>
                    <TableHead>{t('quote_expiry')}</TableHead>
                    <TableHead>{t('quote_total')}</TableHead>
                    <TableHead>{t('quote_status')}</TableHead>
                    <TableHead>PDF</TableHead>
                    <TableHead>Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(q => (
                    <TableRow key={q._id}>
                      <TableCell className="font-medium">{q.quote_number}</TableCell>
                      <TableCell>
                        <div>{q.client_name}</div>
                        {q.source === 'client' && <span className="text-xs text-purple-600">Client</span>}
                      </TableCell>
                      <TableCell className="text-sm">{new Date(q.date).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell className="text-sm">{new Date(q.expiry_date).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell className="font-bold">{q.total_ttc?.toFixed(2)} €</TableCell>
                      <TableCell>
                        {q.status === 'envoyé' ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs">{t('sent')}</Badge>
                        ) : q.source === 'client' ? (
                          <Badge className="bg-violet-100 text-violet-800 border border-violet-200 text-xs">Client</Badge>
                        ) : q.source === 'odoo' ? (
                          <Badge className="bg-purple-100 text-purple-800 border border-purple-200 text-xs">Odoo</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-xs">{t('pending')}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button data-testid={`download-pdf-${q._id}`} size="sm" variant="outline" className="gap-1 h-7 text-xs" onClick={() => handleDownloadPDF(q._id)}>
                          <Download size={14} /> PDF
                        </Button>
                      </TableCell>
                      <TableCell>
                        {q.client_email ? (
                          <Button size="sm" className="gap-1 h-7 text-xs bg-blue-600 hover:bg-blue-700" disabled={sending === q._id} onClick={() => handleSendEmail(q._id)}>
                            <Send size={13} /> {sending === q._id ? '...' : t('send_to_client')}
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              );
            })()}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
